/**
 * Error mixin: every Mochia-defined error class implements `MochiaError`.
 *
 * `hint` is an optional remediation string in 繁體中文. It tells the dev or
 * support engineer what to do next (e.g. "set VITE_OCR_ENDPOINT in .env.local
 * and restart Vite"), rather than just stating the failure.
 *
 * Sharp edges:
 *  - Hints are PRODUCT copy. They surface in toasts / error banners, so they
 *    must be safe to show end-users (no stack traces, no secrets, no env-var
 *    values — only the names).
 *  - Hint presence is checked via `hasHint()` so type-narrowing works without
 *    requiring every catch site to know the concrete subclass.
 */
export interface MochiaError {
  /** Optional remediation hint in 繁體中文. */
  hint?: string;
}

export function hasHint(err: unknown): err is { hint: string } {
  return (
    typeof err === 'object' &&
    err !== null &&
    'hint' in err &&
    typeof (err as { hint?: unknown }).hint === 'string' &&
    ((err as { hint: string }).hint).length > 0
  );
}

/**
 * Thrown when a token contains whitespace or control characters and would
 * therefore break the HTTP `Authorization` header (header injection).
 *
 * Distinct from `SheetsApiError(0, ...)` so callers can route it as a real
 * client-side validation error (status 400) instead of a network failure
 * (status 0). The App-level error handler treats this as "force re-login".
 */
export class InvalidTokenFormatError extends Error implements MochiaError {
  readonly status = 400;
  readonly hint: string;
  constructor(message?: string) {
    super(message ?? 'Token has whitespace or control characters; refusing to build Authorization header.');
    this.name = 'InvalidTokenFormatError';
    this.hint = '存取權杖格式不正確，請重新登入以取得新的權杖。';
  }
}
